import React, { useMemo, useEffect, useState } from 'react';
import { AppProvider as PolarisAppProvider } from '@shopify/polaris';
import createApp from '@shopify/app-bridge';
import { SHOPIFY_API_KEY } from '../config/env';

// App Bridge Context for providing app instance to children
export const AppBridgeContext = React.createContext(null);

/**
 * App Bridge Provider Component
 * Sets up App Bridge React Provider according to Shopify documentation:
 * https://shopify.dev/docs/api/app-bridge/previous-versions/app-bridge-from-npm/using-react
 * 
 * Wraps the app with both Polaris AppProvider and App Bridge Provider
 * as recommended in the documentation.
 */
export const AppBridgeConfigContext = React.createContext(null);

export default function AppBridgeProviderWrapper({ children }) {
  // Get App Bridge config from URL params and environment
  const config = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const host = urlParams.get('host');
    const shop = urlParams.get('shop');
    
    // Get API key from centralized config
    const apiKey = SHOPIFY_API_KEY;
    
    if (!apiKey) {
      console.warn('Shopify API key is not configured');
      return null;
    }
    
    if (!host) {
      console.warn('Host parameter is missing from URL');
      return null;
    }
    
    // AppConfig for createApp:
    // - apiKey: string (required) - The client ID from Shopify Partner Dashboard
    // - host: string (required) - The hostname for the current shop
    // - forceRedirect: boolean (optional) - Defaults to true in production, false in dev
    return {
      apiKey: apiKey,
      host: host,
      forceRedirect: process.env.NODE_ENV === 'production',
      shop: shop, // Store for context (not part of AppConfig but useful)
    };
  }, []);

  // Create App Bridge app instance
  const [app, setApp] = useState(null);

  useEffect(() => {
    if (config) {
      try {
        // Create App Bridge app instance using createApp
        const appInstance = createApp(config);
        setApp(appInstance);
        
        // Set global reference for use in non-React contexts (e.g., apiCall utility)
        // This allows getSessionToken to work properly in Common.js
        if (typeof window !== 'undefined') {
          window.__shopifyAppBridgeInstance = appInstance;
        }
      } catch (error) {
        console.error('Failed to create App Bridge app:', error);
        // Clear global reference on error
        if (typeof window !== 'undefined') {
          window.__shopifyAppBridgeInstance = null;
        }
      }
    } else {
      // Clear global reference if config is not available
      if (typeof window !== 'undefined') {
        window.__shopifyAppBridgeInstance = null;
      }
    }
    
    // Cleanup function to clear global reference on unmount
    return () => {
      if (typeof window !== 'undefined') {
        window.__shopifyAppBridgeInstance = null;
      }
    };
  }, [config]);

  // Create context value for components that need it
  const contextValue = useMemo(() => config, [config]);

  // If config is not available, still render children with Polaris
  // This allows the app to work in development without Shopify context
  if (!config) {
    return (
      <PolarisAppProvider i18n={{}}>
        <AppBridgeConfigContext.Provider value={null}>
          <AppBridgeContext.Provider value={null}>
            {children}
          </AppBridgeContext.Provider>
        </AppBridgeConfigContext.Provider>
      </PolarisAppProvider>
    );
  }

  // Render with Polaris AppProvider and App Bridge context
  // Note: In version 4.x, Provider component doesn't exist
  // We use createApp directly and provide the app instance via context
  return (
    <PolarisAppProvider i18n={{}}>
      <AppBridgeConfigContext.Provider value={contextValue}>
        <AppBridgeContext.Provider value={app}>
          {children}
        </AppBridgeContext.Provider>
      </AppBridgeConfigContext.Provider>
    </PolarisAppProvider>
  );
}

